"use client";

import "@/lib/csrf/axios";
import {
  type DehydratedState,
  HydrationBoundary,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { DesignSystemProvider } from "@zapier/design-system";
import { type AxiosError } from "axios";
import { type Session } from "next-auth";
import { SessionProvider } from "next-auth/react";
import { usePathname, useRouter } from "next/navigation";
import { type ReactNode, useState } from "react";

import { CustomCurrentAccountId } from "@/components/providers/custom-current-account-id";
import { RawBlocksProvider } from "@/components/providers/raw-blocks-provider";
import { SsoHintProvider } from "@/components/providers/sso-hint-provider";
import { ToastViewport } from "@/components/providers/toast-viewport";
import { TourPointsProvider } from "@/components/providers/tour-points-provider";
import { ClientScheduledMaintenanceChecker } from "@/components/scheduled-maintenance/client-scheduled-maintenance-checker";
import { type ReplaceReturnType } from "@/helpers";

const EMBED_LOGIN_ROUTES = [
  "/embed/login-popup",
  "/embed/login-success",
  "/embed/login",
  "/embed/cookie-permission",
];

const handleError = (error: AxiosError) => {
  if (error?.response?.status === 401) {
    const pathname = window.location.pathname;

    // Skip redirect for all embed auth routes, share pages, and login page
    if (
      EMBED_LOGIN_ROUTES.some((route) => pathname.startsWith(route)) ||
      pathname.startsWith("/share") ||
      pathname.startsWith("/login")
    ) {
      return;
    }

    // For embed pages, redirect to embed login with embedId preserved
    if (pathname.startsWith("/embed")) {
      const embedId = new URLSearchParams(window.location.search).get(
        "embedId",
      );
      const redirectUrl = embedId
        ? `/embed/login?embedId=${embedId}`
        : "/embed/login";
      window.location.href = redirectUrl;
      return;
    }

    const here = `${window.location.pathname}${window.location.search}`;
    window.location.href = `/login?redirectTo=${encodeURIComponent(here)}`;
  }
};

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: (failureCount, error: unknown) => {
          const axiosError = error as AxiosError;
          if (
            axiosError?.response?.status === 401 ||
            axiosError?.response?.status === 403 ||
            axiosError?.response?.status === 422
          ) {
            return false;
          }
          return failureCount <= 2;
        },
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (!query.meta?.ignoreGlobalErrorHandler) {
          handleError(error as AxiosError);
        }
      },
    }),
  });
}

const SessionProviderWithNodeReturn = SessionProvider as ReplaceReturnType<
  typeof SessionProvider,
  ReactNode
>;

export function Providers({
  children,
  dehydratedState,
  session,
}: {
  children: React.ReactNode;
  dehydratedState: DehydratedState;
  session: Session | null;
}) {
  const pathname = usePathname();
  const router = useRouter();

  // oxlint-disable-next-line react/hook-use-state -- we don't need the setter
  const [queryClient] = useState(() => makeQueryClient());

  const isExtension = pathname.includes("chrome-extension");

  const isEmbedLoginRoute = EMBED_LOGIN_ROUTES.some((route) =>
    pathname.startsWith(route),
  );

  // For embed login pages, use ultra-minimal providers without SessionProvider to avoid session calls
  if (isEmbedLoginRoute) {
    return (
      <DesignSystemProvider navigate={router.push}>
        <QueryClientProvider client={queryClient}>
          <HydrationBoundary state={dehydratedState}>
            <RawBlocksProvider>{children}</RawBlocksProvider>
          </HydrationBoundary>
        </QueryClientProvider>
      </DesignSystemProvider>
    );
  }

  return (
    <DesignSystemProvider navigate={router.push}>
      <SessionProviderWithNodeReturn
        refetchOnWindowFocus={false}
        session={session}
      >
        <QueryClientProvider client={queryClient}>
          <CustomCurrentAccountId>
            <SsoHintProvider>
              <HydrationBoundary state={dehydratedState}>
                {!isExtension && (
                  <ReactQueryDevtools buttonPosition="bottom-right" />
                )}
                <ClientScheduledMaintenanceChecker />
                <RawBlocksProvider>
                  <TourPointsProvider>{children}</TourPointsProvider>
                </RawBlocksProvider>
                <ToastViewport />
              </HydrationBoundary>
            </SsoHintProvider>
          </CustomCurrentAccountId>
        </QueryClientProvider>
      </SessionProviderWithNodeReturn>
    </DesignSystemProvider>
  );
}

export default Providers;
